/**
 * core-anim.esm.js — WAAPI animation helpers
 *
 * Dual-exports: ES module for ESM consumers and window.CoreAnim global for
 * IIFE consumers in app.min.js. Import via the built runtime name:
 * `import { ... } from './core-anim.js'`.
 *
 * AI: Entirely written by AI
 * Date: 2026-04-24
 * Model: Claude Sonnet 4.6
 */

import { log } from './log.js';

const DEFAULT_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Velocity tolerated empty jQuery collections; the legacy IIFE callers rely on
// that. Match the behavior so `CoreAnim.x($jq[0], …)` is a no-op when the
// selector matched nothing, instead of throwing on `undefined.scrollHeight`.
const NOOP = Promise.resolve();

export function fade(el, { from = 0, to = 1, duration = 200, easing = DEFAULT_EASING } = {}) {
  if (!el) return NOOP;
  return el.animate([{ opacity: from }, { opacity: to }], { duration, easing, fill: 'forwards' })
    .finished;
}

// fill:'forwards' keeps the animation in a "filling" state after it finishes,
// applying its final keyframe values via the WAAPI cascade — which sits ABOVE
// inline styles. Clearing `el.style.height = ''` is therefore a no-op until the
// animation is canceled. We cancel after .finished so callers see the element
// return to its natural CSS-derived sizing. Without this, a slideDown that
// measured scrollHeight while an ancestor was display:none locks the element
// at height: 0 forever.
export function fadeIn(el, { duration = 200, easing = DEFAULT_EASING, display = '' } = {}) {
  if (!el) return NOOP;
  if (display) {
    el.style.display = display;
  } else {
    el.style.display = '';
    // CSS rules (e.g. `display: none` in a stylesheet) aren't cleared by
    // resetting inline display. Force a non-none value so opacity is visible.
    if (getComputedStyle(el).display === 'none') el.style.display = 'block';
  }
  const anim = el.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration,
    easing,
    fill: 'forwards',
  });
  return anim.finished.then(() => {
    el.style.opacity = '';
    anim.cancel();
  });
}

export function fadeOut(el, { duration = 200, easing = DEFAULT_EASING } = {}) {
  if (!el) return NOOP;
  const anim = el.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration,
    easing,
    fill: 'forwards',
  });
  return anim.finished.then(() => {
    el.style.display = 'none';
    el.style.opacity = '';
    anim.cancel();
  });
}

// WAAPI cannot animate to 'auto'; measure scrollHeight first.
// scrollHeight is 0 when an ancestor is display:none — the animation would
// run invisibly and is just busywork, so we skip it and let the element take
// its natural CSS-derived height once the ancestor renders.
export function slideDown(el, { duration = 300, easing = DEFAULT_EASING } = {}) {
  if (!el) return NOOP;
  el.style.display = '';
  if (getComputedStyle(el).display === 'none') el.style.display = 'block';
  const h = el.scrollHeight;
  if (h === 0) return NOOP;
  el.style.overflow = 'hidden';
  const anim = el.animate(
    [
      { height: '0px', opacity: 0 },
      { height: `${h}px`, opacity: 1 },
    ],
    { duration, easing, fill: 'forwards' }
  );
  return anim.finished.then(() => {
    el.style.height = '';
    el.style.overflow = '';
    el.style.opacity = '';
    anim.cancel();
  });
}

export function slideUp(el, { duration = 300, easing = DEFAULT_EASING } = {}) {
  if (!el) return NOOP;
  const h = el.scrollHeight;
  if (h === 0) {
    // Already collapsed (or ancestor hidden). Match the post-animation state.
    el.style.display = 'none';
    return NOOP;
  }
  el.style.overflow = 'hidden';
  const anim = el.animate(
    [
      { height: `${h}px`, opacity: 1 },
      { height: '0px', opacity: 0 },
    ],
    { duration, easing, fill: 'forwards' }
  );
  return anim.finished.then(() => {
    el.style.display = 'none';
    el.style.height = '';
    el.style.overflow = '';
    el.style.opacity = '';
    anim.cancel();
  });
}

function staggered(
  els,
  keyframes,
  { duration = 300, easing = DEFAULT_EASING, stagger = 50, delay = 0 } = {}
) {
  if (!els) return NOOP;
  return Promise.all(
    Array.from(els).map(
      (el, i) =>
        el.animate(keyframes, { duration, easing, delay: delay + i * stagger, fill: 'both' })
          .finished
    )
  );
}

export function slideRightIn(els, opts = {}) {
  return staggered(
    els,
    [
      { transform: 'translateX(20px)', opacity: 0 },
      { transform: 'translateX(0)', opacity: 1 },
    ],
    { stagger: 45, ...opts }
  );
}

export function slideUpOut(els, opts = {}) {
  return staggered(
    els,
    [
      { transform: 'translateY(0)', opacity: 1 },
      { transform: 'translateY(-20px)', opacity: 0 },
    ],
    opts
  );
}

export function slideUpIn(els, opts = {}) {
  return staggered(
    els,
    [
      { transform: 'translateY(20px)', opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 },
    ],
    opts
  );
}

export function stop(el) {
  if (!el) return;
  el.getAnimations().forEach(a => a.cancel());
}

// Native smooth scroll has no completion event, so the callback fires on a
// duration-based timeout (approximate).
export function scrollTo(el, { offset = 0, duration = 500, complete } = {}) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({ top, behavior: 'smooth' });
  if (complete) setTimeout(complete, duration);
}

export function scrollToTop({ duration = 500, complete } = {}) {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  if (complete) setTimeout(complete, duration);
}

window.CoreAnim = {
  fade,
  fadeIn,
  fadeOut,
  slideDown,
  slideUp,
  slideRightIn,
  slideUpOut,
  slideUpIn,
  stop,
  scrollTo,
  scrollToTop,
};

log('[CoreAnim] loaded');
